import { useEffect, useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import { fmtDateTime } from '../../../lib/util';
import type { Case } from '../../../domain/initial-cases';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { downloadIntakeFile, listIntakeFiles, type IntakeFile } from '../../../api/intake';

// M-13: TNs are the payload BCID vets — validate E.164 before commit so a
// typo can't become a permanent audit-trail entry. Separators analysts
// commonly paste (spaces, dashes, dots, parens) are stripped first.
// #620 - the lower bound is 8 digits after the `+`, not E.164's formal 2:
// `+44` on its own is a paste error every time. Held equal to the API's copy
// (apps/api/src/routes/intake.ts) by a test here that reads it off disk.
export const E164 = /^\+[1-9]\d{7,14}$/;
// #611 / #565 - one COUNTRY-SPECIFIC rule layered on top of the generic gate
// above, which stays broad because national plans differ. The cost is that a US
// number with a digit dropped - `+1202555012` - is indistinguishable from a
// legitimately shorter international number, so it was accepted here, attested
// to CTIA, and never branded. `+1` is the NANP, whose numbers carry exactly ten
// digits after the country code, so that ONE plan is asserted precisely.
//
// #565 added this to the intake API and the customer portal but not here, so an
// analyst could still type on a case what a customer could no longer submit. The
// three copies are pinned equal by tests that read the API declarations off disk
// (TnsTab.test.tsx here, apps/portal/src/lib/validation.test.ts there).
export const NANP_E164 = /^\+1\d{10}$/;
export const normalizeTn = (raw: string): string => raw.replace(/[\s\-().]/g, '');
// ONE definition of the country-scoped rule, used by every path that admits a
// number to a case - the manual add and the onboarding-declaration copy - so the
// two cannot diverge, and asked BEFORE the generic gate so a number that claims
// `+1` is told what is actually wrong with it rather than being handed the
// generic "use the full international form".
const nanpProblem = (tn: string): boolean => tn.startsWith('+1') && !NANP_E164.test(tn);
export const isValidTn = (tn: string): boolean => !nanpProblem(tn) && E164.test(tn);

// Issue #115: the onboarding form captures TNs as a free-text, comma-separated
// declaration (onboarding.data.tns), but settlement delivery reads only this
// canonical analyst-vetted list. Nothing copies one to the other, so a case
// whose numbers were only typed into the onboarding form dead-letters with
// "no TNs to deliver". Parse the declaration through the same E.164 normalizer
// used for manual adds, keeping only well-formed numbers and de-duping — the
// analyst still clicks to copy, so the "registrant declared" vs. "we vetted and
// attest to CTIA" separation stays intact. Non-string (seeded rows carry a
// malformed `{}`) yields nothing, same as no declaration.
export const parseDeclaredTns = (raw: unknown): string[] => {
  if (typeof raw !== 'string') return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const tn = normalizeTn(part);
    if (isValidTn(tn) && !seen.has(tn)) { seen.add(tn); out.push(tn); }
  }
  return out;
};

export function TnsTab({ c }: { c: Case }) {
  const { updateCase } = useAppData();
  const [tnInput, setTnInput] = useState('');
  const [tnError, setTnError] = useState<string | null>(null);
  // Fresh cases come back from POST /cases with `tns: {}` because the schema
  // default is the empty-object literal — see migration 0002. Until that
  // backfill runs against every environment, defend at the read site.
  const tns: string[] = Array.isArray(c.tns) ? c.tns : [];
  // Coerce both flags to booleans so spreads back into updateCase satisfy the
  // Case.loa shape `{ required: boolean; verified: boolean }`.
  //
  // #775 — and every other key the stored value carries goes back with them.
  // `loa` is written WHOLESALE, and this used to send only the two flags, which
  // dropped whatever else was there: the Onboarding tab's LOA details, and the
  // customer's `notProvidedBy382` marker, which the API refuses to see dropped.
  const stored = (c.loa ?? {}) as Record<string, unknown>;
  const loa = {
    ...stored,
    required: !!stored.required,
    verified: !!stored.verified,
  };
  // #775 — the customer declared numbers 382 does not provide and sent the
  // signed letter with the application. The requirement is theirs, so it
  // cannot be unticked here; the case cannot enter vetting or be approved until
  // "LOA verified" is ticked.
  const customerDeclared = stored.notProvidedBy382 === true;
  const declaredOn = typeof stored.submissionId === 'string' ? stored.submissionId : null;
  // #834 — on a case promoted from an application, the customer can send a
  // replacement letter until this one is verified, so "LOA verified" names the
  // letter checked (`verifiedFileId`) and the API refuses (409) anything but the
  // newest. The letters are listed here, and the tick waits for them. Fetched
  // again when `verified` changes, so a refused tick (rolled back) shows the
  // letter that arrived meanwhile.
  const namesLetter = customerDeclared && declaredOn !== null;
  const [letters, setLetters] = useState<IntakeFile[] | null>(null);
  const [lettersError, setLettersError] = useState<string | null>(null);
  // A failed Open is reported beside the list, never in place of it: the tick
  // names the newest letter, so it is offered only while that letter is shown.
  const [openError, setOpenError] = useState<string | null>(null);
  useEffect(() => {
    if (!namesLetter || declaredOn === null) return;
    let live = true;
    setLetters(null);
    setLettersError(null);
    setOpenError(null);
    listIntakeFiles({ submissionId: declaredOn })
      .then((rows) => { if (live) setLetters(rows); })
      .catch((err: unknown) => { if (live) setLettersError(err instanceof Error ? err.message : String(err)); });
    return () => { live = false; };
  }, [namesLetter, declaredOn, loa.verified]);
  // The newest is the highest id, which the API checks and keeps the last attached (#840). A failed
  // fetch leaves `letters` null (each fetch starts from null), so the tick stays disabled.
  const newest = letters && letters.length > 0
    ? letters.reduce((a, b) => (b.id > a.id ? b : a))
    : null;
  const openLetter = (f: IntakeFile) => {
    setOpenError(null);
    downloadIntakeFile(f).catch((err: unknown) => setOpenError(err instanceof Error ? err.message : String(err)));
  };
  // Numbers the registrant declared on the onboarding form that parse to valid
  // E.164 and aren't already on the canonical list. The copy affordance only
  // surfaces when the canonical list is empty (issue #115).
  const declaredTns = parseDeclaredTns(c.onboarding?.data?.tns).filter(tn => !tns.includes(tn));
  const copyDeclaredTns = () => {
    if (declaredTns.length === 0) return;
    // One PATCH appending every normalized number — same-tick case PATCHes are
    // serialized per case (If-Match chaining), so a per-TN loop would stall.
    updateCase(c.id, {
      tns: [...tns, ...declaredTns],
      action: { code: 'TN_ADDED', detail: { tns: declaredTns, count: declaredTns.length, source: 'onboarding' } },
    });
  };
  const addTn = () => {
    const tn = normalizeTn(tnInput);
    if (!tn) return;
    // Named rather than described: with a mistyped number the analyst needs to
    // see WHICH one came back, the same way the duplicate refusal below names it.
    if (nanpProblem(tn)) { setTnError(`${tn} is not a valid North American number — a +1 number needs exactly ten digits after the country code, e.g. +18005551234.`); return; }
    if (!E164.test(tn)) { setTnError('Not a valid E.164 number — use the full international form, with at least 8 digits after the +, e.g. +18005551234.'); return; }
    if (tns.includes(tn)) { setTnError(`${tn} is already on this case.`); return; }
    updateCase(c.id, {
      tns: [...tns, tn],
      action: { code: 'TN_ADDED', detail: { tn } },
    });
    setTnInput('');
    setTnError(null);
  };
  return (
    <div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Telephone Number Validation</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Validate TNs via carrier invoices, Responsible Org records, Free Caller Registry, First Orion, LERG, Somos, LNPA</div>
        <div style={{ marginBottom: 12 }}>
          {tns.map((tn: string, i: number) => (
            <span key={i} style={{ ...styles.tag, background: C.goldBg, color: C.goldText, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {tn}
              <button
                type="button"
                aria-label={`Remove TN ${tn}`}
                style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 700, lineHeight: 1 }}
                onClick={() => updateCase(c.id, {
                  tns: tns.filter((_, j) => j !== i),
                  action: { code: 'TN_REMOVED', detail: { tn } },
                })}
              >×</button>
            </span>
          ))}
          {tns.length === 0 && <span style={{ color: C.textDim }}>No TNs added</span>}
        </div>
        {tns.length === 0 && declaredTns.length > 0 && (
          <div style={{ marginBottom: 12, padding: 10, background: C.goldBg, borderRadius: 8, fontSize: 12 }}>
            <div style={{ color: C.textMuted, marginBottom: 8 }}>
              The registrant declared telephone number(s) on the onboarding form that aren't on this vetted list. Settlement delivery uses this list only — copy them here to attest, then verify each.
            </div>
            <button type="button" style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={copyDeclaredTns}>
              Copy declared TNs from onboarding ({declaredTns.length} found)
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            id="tn-input"
            aria-label="Telephone number to add"
            style={{ ...styles.input, ...(tnError ? { borderColor: C.danger } : {}) }}
            placeholder="Add TN in E.164 form, e.g. +18005551234"
            value={tnInput}
            aria-invalid={tnError ? true : undefined}
            aria-describedby={tnError ? 'tn-input-error' : undefined}
            onChange={e => { setTnInput(e.target.value); if (tnError) setTnError(null); }}
            onKeyDown={e => { if (e.key === 'Enter') addTn(); }}
          />
          <button type="button" style={{ ...styles.btn, ...styles.btnGold }} onClick={addTn}>Add</button>
        </div>
        {tnError && <div id="tn-input-error" role="alert" style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>{tnError}</div>}
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Letter of Authorization (LOA)</h3>
        {customerDeclared && (
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }} role="note">
            The customer declared that one or more numbers is not provided by 382 and sent the signed LOA with
            {declaredOn ? <> application <code>{declaredOn}</code></> : ' the application'}.
            {namesLetter ? ' Open it below to check it.' : ' Open it from the Intake Queue to check it.'} The case
            cannot enter vetting or be approved until the LOA is verified. Until then the customer can send a
            replacement from the portal: the newest letter on the application is the one to check
            {namesLetter ? ', and ticking "LOA verified" records that letter. If a newer one arrives first, the tick is refused.' : '.'}
          </div>
        )}
        {namesLetter && (
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            {lettersError
              ? <div role="alert" style={{ color: C.danger }}>Could not load the LOA: {lettersError}</div>
              : letters === null
                ? <span style={{ color: C.textMuted }}>Loading the LOA…</span>
                : newest === null
                  ? <span style={{ color: C.danger }}>No LOA file is attached to the application.</span>
                  : (
                    <ul aria-label="LOA letters" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {letters.map((f) => (
                        <li key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontWeight: f === newest ? 600 : 400, color: f === newest ? C.text : C.textMuted }}>
                            {f.filename}
                          </span>
                          <span style={{ color: C.textDim }}>{fmtDateTime(f.uploadedAt)}</span>
                          {f === newest
                            ? <span style={{ ...styles.tag, background: C.successBg, color: C.success }}>newest</span>
                            : <span style={{ ...styles.tag }}>replaced</span>}
                          {loa.verified && stored.verifiedFileId === f.id && <span style={{ ...styles.tag }}>verified</span>}
                          <button
                            type="button"
                            aria-label={`Open ${f.filename}`}
                            style={{ ...styles.btnUnstyled, color: C.goldText, textDecoration: 'underline', fontSize: 12 }}
                            onClick={() => openLetter(f)}
                          >Open</button>
                        </li>
                      ))}
                    </ul>
                  )}
            {openError && <div role="alert" style={{ color: C.danger }}>Could not open the LOA: {openError}</div>}
          </div>
        )}
        <div style={styles.checkItem}>
          <input
            type="checkbox"
            aria-label="LOA required"
            checked={!!loa.required}
            disabled={customerDeclared}
            onChange={e => updateCase(c.id, { loa: { ...loa, required: e.target.checked } })}
          />
          <span>LOA required (registrant acting on behalf of another entity{customerDeclared ? ', or numbers not provided by 382' : ''})</span>
        </div>
        {loa.required && (
          <div style={{ ...styles.checkItem, marginTop: 8 }}>
            <input
              type="checkbox"
              aria-label="LOA verified"
              checked={!!loa.verified}
              // #834 — a tick names the newest letter, so it waits for the list.
              disabled={namesLetter && !loa.verified && newest === null}
              onChange={e => {
                const fileId = namesLetter && e.target.checked ? newest?.id : undefined;
                if (namesLetter && e.target.checked && fileId === undefined) return;
                updateCase(c.id, {
                  loa: { ...loa, verified: e.target.checked, ...(fileId !== undefined ? { verifiedFileId: fileId } : {}) },
                  action: {
                    code: 'LOA_VERIFIED',
                    detail: { verified: e.target.checked, source: 'tns-loa', ...(fileId !== undefined ? { fileId } : {}) },
                  },
                });
              }}
            />
            <span>LOA verified — contacted authorizing entity, confirmed authority and TN assignment</span>
          </div>
        )}
      </div>
    </div>
  );
}
